import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/components/auth/AuthProvider';
import UserMenu from '@/components/UserMenu';
import TokenCounter from '@/components/TokenCounter';
import { Button } from '@/components/ui/button';
import Logo from '@/components/brand/Logo';

interface HeaderProps {
  onMobileMenuToggle?: () => void;
  showMobileMenu?: boolean;
  tone?: 'default' | 'cover';
}

export default function Header({ onMobileMenuToggle, showMobileMenu = false, tone = 'default' }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const cover = tone === 'cover';

  const navLink = cover
    ? 'px-3 py-2 text-[13px] text-white/70 hover:text-white'
    : 'px-3 py-2 text-[13px] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]';

  return (
    <header className={cover
      ? 'absolute top-0 left-0 right-0 z-50 w-full border-b border-white/10 bg-transparent'
      : 'sticky top-0 z-50 w-full border-b border-[var(--theme-border)] bg-[var(--header-bg)] backdrop-blur-md'
    }>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <button
              onClick={onMobileMenuToggle}
              className={cover
                ? 'md:hidden p-2 text-white/80 hover:text-white'
                : 'md:hidden p-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]'}
              aria-label="Toggle menu"
            >
              {showMobileMenu ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>

            <Link to="/" className="flex items-center">
              <Logo wordmark inverse={cover} />
            </Link>
          </div>

          <nav className="hidden md:flex items-center">
            <Link to="/studio" className={navLink}>Studio</Link>
            <Link to="/pricing" className={navLink}>Tarifs</Link>
            <Link to="/blog" className={navLink}>Journal</Link>
            <Link to="/contact" className={navLink}>Contact</Link>
            {user && (
              <>
                <Link to="/settings" className={navLink}>Paramètres</Link>
                <Link to="/billing" className={navLink}>Facturation</Link>
              </>
            )}
          </nav>

          <div className="flex items-center gap-1">
            {user && (
              <div className="hidden sm:block">
                <TokenCounter />
              </div>
            )}

            <button
              onClick={toggleTheme}
              className={cover
                ? 'p-2 text-white/70 hover:text-white'
                : 'p-2 text-[var(--theme-text-tertiary)] hover:text-[var(--theme-text-primary)]'}
              aria-label={`Passer en mode ${theme === 'light' ? 'sombre' : 'clair'}`}
              title={`Mode ${theme === 'light' ? 'sombre' : 'clair'}`}
            >
              {theme === 'light' ? <Moon className="w-4 h-4" strokeWidth={1.5} /> : <Sun className="w-4 h-4" strokeWidth={1.5} />}
            </button>

            {user ? (
              <UserMenu />
            ) : (
              <Link to="/login">
                <Button size="sm" variant={cover ? 'outline' : 'default'} className={cover ? 'text-white border-white/30 hover:bg-white/10' : ''}>
                  Connexion
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
